import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BrainCircuit, Sparkles, KeyRound, GraduationCap } from "lucide-react";
import { api } from "@/context/AuthContext";

const RANKS = [
  { min: 0, en: "Fresh Circuit", es: "Circuito Nuevo" },
  { min: 1, en: "Little Spark", es: "Chispita" },
  { min: 3, en: "Curious Mind", es: "Mente Curiosa" },
  { min: 6, en: "Mind Explorer", es: "Explorador Mental" },
  { min: 10, en: "Brainiac", es: "Cerebrito" },
  { min: 15, en: "Digital Sage", es: "Sabio Digital" },
  { min: 20, en: "Quantum Genius", es: "Genio Cuántico" },
];

const T = {
  en: {
    tag: "The Most Important Trait",
    title: "Feed Your Creature's INTELLIGENCE",
    explain: "Every 8 days your parents or teachers will give you a special code. Type it here and your creature gains +1 INTELLIGENCE level. The more codes you feed it, the smarter and more famous it becomes! Each code works only once, so use it before anyone else.",
    level: "Intelligence Level",
    codesEaten: "codes redeemed",
    nextRank: "Next rank",
    maxRank: "Maximum rank reached!",
    placeholder: "TYPE YOUR CODE HERE",
    btn: "Feed Intelligence",
    busy: "Feeding...",
    success: "+1 INTELLIGENCE! Your creature just got smarter 🧠",
    invalid: "That code doesn't exist. Check the spelling or ask your parents or teachers for another one.",
    used: "Oops! That code was already eaten by another creature. Ask for a new one.",
    empty: "Type a code first!",
    history: "Codes your creature already ate",
    noHistory: "No codes yet. Ask your parents or teachers for your first one!",
  },
  es: {
    tag: "El Rasgo Más Importante",
    title: "Alimenta la INTELIGENCIA de tu Criatura",
    explain: "Cada 8 días tus papás o maestros te darán un código especial. Escríbelo aquí y tu criatura ganará +1 nivel de INTELIGENCIA. ¡Entre más códigos le des, más inteligente y famosa se vuelve! Cada código funciona solo una vez, así que úsalo antes que nadie.",
    level: "Nivel de Inteligencia",
    codesEaten: "códigos canjeados",
    nextRank: "Siguiente rango",
    maxRank: "¡Rango máximo alcanzado!",
    placeholder: "ESCRIBE TU CÓDIGO AQUÍ",
    btn: "Alimentar Inteligencia",
    busy: "Alimentando...",
    success: "¡+1 INTELIGENCIA! Tu criatura acaba de volverse más lista 🧠",
    invalid: "Ese código no existe. Revisa que esté bien escrito o pide otro a tus papás o maestros.",
    used: "¡Ups! Ese código ya se lo comió otra criatura. Pide uno nuevo.",
    empty: "¡Primero escribe un código!",
    history: "Códigos que ya se comió tu criatura",
    noHistory: "Aún no hay códigos. ¡Pide el primero a tus papás o maestros!",
  },
};

const rankFor = (level, lang) => {
  let current = RANKS[0];
  let next = null;
  for (let i = 0; i < RANKS.length; i++) {
    if (level >= RANKS[i].min) current = RANKS[i];
    else { next = RANKS[i]; break; }
  }
  return { current: current[lang] || current.en, next, nextName: next ? (next[lang] || next.en) : null };
};

export const IntelligenceLab = ({ lang }) => {
  const t = T[lang] || T.en;
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get("/intelligence/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const redeem = async () => {
    setError(null);
    if (!code.trim()) { setError(t.empty); return; }
    setBusy(true);
    try {
      await api.post("/intelligence/redeem", { code });
      toast.success(t.success);
      setCode("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d === "code_used" ? t.used : d === "invalid_code" ? t.invalid : t.invalid);
    } finally {
      setBusy(false);
    }
  };

  const level = status?.intelligence ?? 0;
  const { current, next, nextName } = rankFor(level, lang);
  const prevMin = [...RANKS].reverse().find((r) => level >= r.min)?.min ?? 0;
  const pct = next ? Math.min(100, Math.round(((level - prevMin) / (next.min - prevMin)) * 100)) : 100;

  return (
    <div className="relative bg-[#0F0F1A]/85 border border-[#00F5FF]/30 rounded-3xl p-6 md:p-7 mb-8 overflow-hidden shadow-[0_0_40px_rgba(0,245,255,0.12)]" data-testid="intel-lab">
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-[#00F5FF] blur-[110px] opacity-15 pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-[#B026FF] blur-[110px] opacity-10 pointer-events-none" />

      <span className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.25em] text-[#00F5FF] bg-[#00F5FF]/10 border border-[#00F5FF]/40 px-3 py-1.5 rounded-full mb-4">
        <Sparkles size={10} /> {t.tag}
      </span>

      <div className="flex flex-col lg:flex-row gap-7">
        <div className="lg:w-1/2">
          <h2 className="font-heading text-lg md:text-xl font-black text-white flex items-center gap-2.5 mb-3">
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#00F5FF] border border-[#00F5FF]/40 bg-[#00F5FF]/10 shadow-[0_0_18px_rgba(0,245,255,0.3)] shrink-0">
              <BrainCircuit size={20} />
            </span>
            🧠 {t.title}
          </h2>
          <p className="text-xs text-[#A0A0B0] leading-relaxed flex items-start gap-2 bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3 mb-5" data-testid="intel-explain">
            <GraduationCap size={26} className="text-[#00F5FF] shrink-0 mt-0.5" />
            {t.explain}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <KeyRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00F5FF]/60" />
              <input
                data-testid="intel-code-input"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
                onKeyDown={(e) => e.key === "Enter" && !busy && redeem()}
                placeholder={t.placeholder}
                maxLength={64}
                className="w-full bg-[#05050A]/80 border border-[#00F5FF]/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-black tracking-[0.15em] text-white placeholder:text-white/25 placeholder:tracking-widest focus:outline-none focus:border-[#00F5FF] transition-colors uppercase"
              />
            </div>
            <button
              data-testid="intel-redeem-btn"
              onClick={redeem}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-[11px] font-black uppercase tracking-widest text-[#05050A] bg-gradient-to-r from-[#00F5FF] to-[#00FFAA] shadow-[0_0_20px_rgba(0,245,255,0.4)] hover:shadow-[0_0_32px_rgba(0,245,255,0.65)] hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0"
            >
              <BrainCircuit size={15} />
              {busy ? t.busy : t.btn}
            </button>
          </div>
          {error && (
            <p className="text-[11px] font-bold text-[#FF6E6E] bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-xl px-4 py-2.5 mt-3" data-testid="intel-error">
              {error}
            </p>
          )}
        </div>

        <div className="lg:w-1/2 bg-[#05050A]/70 border border-white/10 rounded-2xl p-5">
          <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40 mb-2">{t.level}</p>
          <div className="flex items-end gap-3 mb-1">
            <p className="font-heading text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00F5FF] to-[#00FFAA]" data-testid="intel-level">{level}</p>
            <p className="text-[10px] text-white/40 font-bold pb-2">{level} {t.codesEaten}</p>
          </div>
          <p className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-[#00F5FF] bg-[#00F5FF]/10 border border-[#00F5FF]/40 px-3.5 py-1.5 rounded-full mb-4" data-testid="intel-rank">
            <Sparkles size={11} /> {current}
          </p>
          <div className="h-2.5 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-2">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#00F5FF] to-[#00FFAA] shadow-[0_0_10px_rgba(0,245,255,0.6)] transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-white/40 font-bold mb-4" data-testid="intel-next-rank">
            {next ? `${t.nextRank}: ${nextName} (${next.min})` : t.maxRank}
          </p>

          <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40 mb-2">{t.history}</p>
          <div className="flex flex-wrap gap-2" data-testid="intel-history">
            {(status?.redemptions || []).length === 0 && <p className="text-[11px] text-white/30">{t.noHistory}</p>}
            {(status?.redemptions || []).slice(0, 8).map((r, i) => (
              <span key={i} className="text-[10px] font-bold text-white/70 bg-[#00F5FF]/8 border border-[#00F5FF]/25 px-3 py-1.5 rounded-full">
                🧠 {r.code}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
